import axios from 'axios';
import { API_CONFIG } from '../config/api';

export interface SyncLog {
  id: string;
  created: number;
  updated: number;
  unrecognized: number;
  skipped: number;
  total: number;
  created_at: string;
}

export interface SyncLogsInfo {
  id: string;
  created_at: string;
  syncItems: {
    id: string;
    log_id: string;
    contract_id: string;
    status: string;
    comment: string;
  }[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedResponseLogsInfo<T> {
  data: T;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include JWT token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('acasa_auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('acasa_auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const getSyncLogs = async (page: number = 1, limit: number = 50): Promise<PaginatedResponse<SyncLog>> => {
  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.SYNC_LOGS}?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sync logs:', error);
    throw error;
  }
};

export const getSyncLogsInfo = async (id: string, page: number = 1, limit: number = 50, filter?: string, statusFilter?: string): Promise<PaginatedResponseLogsInfo<SyncLogsInfo>> => {
  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filter && filter.trim()) {
      params.append('filter', filter.trim());
    }
    
    if (statusFilter && statusFilter.trim()) {
      params.append('status', statusFilter.trim());
    }
    
    const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.LOGS_INFO}/${id}?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sync logs info:', error);
    throw error;
  }
};

export const getDateRange = async (): Promise<{ minDate: string; maxDate: string }> => {
  try {
    const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.DATE_RANGE}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching date range:', error);
    throw error;
  }
};

export const searchSyncLogs = async (
  startDate?: string, 
  endDate?: string, 
  page: number = 1, 
  limit: number = 50
): Promise<PaginatedResponse<SyncLog>> => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    const response = await apiClient.get(`/logs?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error searching sync logs:', error);
    throw error;
  }
};

export const manualSync = async (): Promise<{ status: string }> => {
  try {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.SYNC);
    return response.data;
  } catch (error) {
    console.error('Error during manual sync:', error);
    throw error;
  }
}; 